import type { BuiltList, List } from './Checklist.types';
import {
  analyzeListEvents,
  type ListProjectionInputs,
} from './Checklist.projection';
import { initializeBuiltList, listEventReducer } from './Checklist.reducer';

// ---------------------------------------------------------------------------
// Dependency resolution
// ---------------------------------------------------------------------------

/**
 * Resolve template dependencies for a list, building each dependency.
 * If the consuming list is closed, dependencies are "frozen" at the close
 * date: only events created at or before the close date are included.
 * The freeze cutoff propagates through the entire transitive chain so
 * indirect templates (T1 -> T2 -> closed-checklist) also get frozen.
 * If the list is open, dependencies use all their events (latest version).
 *
 * Includes circular dependency prevention.
 */
export function resolveDependencies(
  projection: ListProjectionInputs,
  allLists: List[],
  /** Used internally to prevent circular dependencies */
  visited: Set<string> = new Set(),
  /**
   * Cutoff propagated from the top-level closed list. Once set it overrides
   * any sub-projection close dates; an open template embedded inside a
   * closed checklist must still be frozen at the checklist's close date.
   */
  freezeCutoff?: Date,
): Record<string, BuiltList> {
  const dependencies: Record<string, BuiltList> = {};
  const cutoff = freezeCutoff ?? projection.closeDate ?? undefined;

  for (const depId of projection.dependencyIds) {
    if (visited.has(depId)) {
      // Circular dependency detected - skip
      continue;
    }

    const depList = allLists.find((l) => l.id === depId);
    if (!depList) {
      throw new Error(`Could not find dependency (${depId})`);
    }

    let effectiveEvents = depList.events;
    if (cutoff) {
      const cutoffMs = cutoff.getTime();
      effectiveEvents = depList.events.filter(
        (e) => e.created.getTime() <= cutoffMs,
      );
    }

    const effectiveDepList: List = { ...depList, events: effectiveEvents };

    const newVisited = new Set(visited);
    newVisited.add(depId);
    const depProjection = analyzeListEvents(effectiveDepList.events);
    const subDeps = resolveDependencies(
      depProjection,
      allLists,
      newVisited,
      cutoff,
    );
    dependencies[depId] = buildListCore(effectiveDepList, subDeps);
  }

  return dependencies;
}

// ---------------------------------------------------------------------------
// Core build (pure reduction with pre-resolved dependencies)
// ---------------------------------------------------------------------------

/**
 * Pure reduction step: reduces a list's events against pre-resolved
 * dependencies. Knows nothing about close dates, freezing, or
 * dependency resolution -- all of that is handled upstream.
 *
 * AUTHORITY CONTRACT: `dependencies` and `dependants`
 *
 * These columns on `qa.lists` are denormalized projections maintained
 * exclusively by DB triggers. The reducer initializes them as empty
 * arrays (see `initializeBuiltList`) and never updates them through
 * event reduction.
 *
 * Application code MUST NOT write these columns. See
 * `apps/api/src/controllers/lists.ts` `listUpsertFields` for the
 * matching write-path constraint and the trigger-ownership comments
 * on `LIST_SELECT_FIELDS`.
 */
export function buildListCore(
  list: List,
  dependencies: Record<string, BuiltList>,
): BuiltList {
  const initial = initializeBuiltList(list.id);

  // Cheap assertion: events must be applied in chronological order.
  // The API sorts events by `created` server-side; if we ever see an
  // out-of-order pair here, either the sort got dropped or events were
  // appended client-side without preserving order. Applying out of order
  // causes structural events (notably REMOVE) to silently no-op when their
  // target doesn't exist yet, producing the "removed-but-still-visible"
  // class of bug.
  let prevCreated = -Infinity;
  for (const event of list.events) {
    const t =
      event.created instanceof Date
        ? event.created.getTime()
        : new Date(event.created).getTime();
    if (t < prevCreated) {
      throw new Error(
        `Out-of-order events while building list ${list.id}: event ${event.id} (${event.type}) at ${new Date(t).toISOString()} precedes a later-applied event.`,
      );
    }
    prevCreated = t;
  }

  const reduced = list.events.reduce(
    (state, event) => listEventReducer(state, event, dependencies),
    initial,
  );

  return {
    ...reduced,
    dependencies: list.dependencies,
    dependants: list.dependants,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Main entry point for building a list.
 * Orchestrates the three-step pattern:
 *   1. Projection  - analyzeListEvents
 *   2. Resolution  - resolveDependencies
 *   3. Reduction   - buildListCore
 *
 * @param list - The list to build
 * @param allLists - All available lists (for dependency resolution)
 * @returns The fully built list with tree structure
 */
export function buildList(list: List, allLists: List[]): BuiltList {
  // 1. Projection: analyze events to extract dependency IDs and close date
  const projection = analyzeListEvents(list.events);

  // 2. Resolution: recursively build each dependency
  const visited = new Set<string>([list.id]);
  const dependencies = resolveDependencies(projection, allLists, visited);

  // 3. Reduction: apply events with resolved dependencies
  return buildListCore(list, dependencies);
}
